/**
 * A210C 串口抢占协调器（跟随焦点模型：哪个窗口活跃哪个持有串口）
 *
 * 两条通道并行，协议完全同构（与桌面 App / wilwei 调试页一致）：
 *  - BroadcastChannel('a210c_serial_port')：协调同浏览器同源的多个标签页；
 *  - WebSocket ws://127.0.0.1:47291：连到桌面 App main 进程内的中继，跨进程与 App 互让。
 *
 * 消息协议：
 *  - release_port   我要占用，请让出
 *  - port_released  我已让出（让出方在真正关闭串口后回执）
 *  - port_busy      App 前台正在用串口，请求方应退避不要强开
 *  - peer_left      App 离开中继（关闭/崩溃），活跃端可立即接管
 *
 * App 未运行或后启动时静默指数退避重连（0.5s 起，上限 30s），确保 Web 先占线时 App 启动后也能抢占。
 * ⚠ 页面若以 https 打开，浏览器会按 mixed-content 拦截 ws://127.0.0.1（http/localhost 不受影响）。
 */

export type ReleaseOutcome = 'ok' | 'busy'

type CoordMsg = { type: 'release_port' | 'port_released' | 'port_busy' | 'peer_left' }

const CHANNEL_NAME = 'a210c_serial_port'
const SERIAL_BRIDGE_PORT = 47291
/** 让出方：关完串口后再等 OS 真正释放，才回执 port_released */
const RELEASE_SETTLE_MS = 700
/** 请求方：收到回执后再等系统完成端口关闭 */
const REPLY_SETTLE_MS = 300
/** 没有任何一端响应时超时直接继续（对端可能根本不存在） */
const RELEASE_TIMEOUT_MS = 800
const WS_RECONNECT_BASE_MS = 500
// 上限 30s：App 退出后浏览器会对每次重连失败原生打一条错误日志，退避放缓减少刷屏
const WS_RECONNECT_MAX_MS = 30000

export interface SerialCoordinatorHandlers {
  /** 收到对端 release_port：让出串口（关闭连接、暂停自动重连）。完成后协调器自动回执 port_released */
  onYieldRequested: () => void | Promise<void>
  /** 本窗口变为活跃（focus / 重新可见）或对端离开：应立即尝试夺回串口 */
  onBecameActive: () => void
  /** 收到 port_busy（App 前台占用）：退避 */
  onBusy?: () => void
}

export interface SerialCoordinator {
  /** 通知其它标签页 / App 让出串口。'ok' = 可以打开；'busy' = App 前台占用，应退避 */
  requestRelease: () => Promise<ReleaseOutcome>
  dispose: () => void
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms))
}

export function createSerialCoordinator(handlers: SerialCoordinatorHandlers): SerialCoordinator {
  const channel = new BroadcastChannel(CHANNEL_NAME)

  /* ---- 到 App 的 WebSocket（按需连接） ---- */
  let ws: WebSocket | null = null
  let wsReconnectTimer: ReturnType<typeof setTimeout> | null = null
  let wsReconnectDelay = WS_RECONNECT_BASE_MS
  let disposed = false

  function connectBridge(): void {
    if (
      disposed ||
      wsReconnectTimer ||
      (ws &&
        (ws.readyState === WebSocket.CONNECTING || ws.readyState === WebSocket.OPEN))
    ) {
      return
    }
    let socket: WebSocket
    try {
      socket = new WebSocket(`ws://127.0.0.1:${SERIAL_BRIDGE_PORT}`)
    } catch {
      return
    }
    ws = socket
    socket.onopen = () => {
      wsReconnectDelay = WS_RECONNECT_BASE_MS
    }
    socket.onmessage = (ev) => {
      let msg: CoordMsg
      try {
        msg = JSON.parse(ev.data)
      } catch {
        return
      }
      handleCoordMsg(msg)
    }
    socket.onclose = () => {
      if (ws === socket) ws = null
      scheduleReconnect()
    }
    socket.onerror = () => {
      try {
        socket.close()
      } catch {
        /* ignore */
      }
    }
  }

  function scheduleReconnect(): void {
    if (disposed || wsReconnectTimer) return
    wsReconnectTimer = setTimeout(() => {
      wsReconnectTimer = null
      connectBridge()
    }, wsReconnectDelay)
    wsReconnectDelay = Math.min(wsReconnectDelay * 2, WS_RECONNECT_MAX_MS)
  }

  /** 同时向标签页 + App 广播 */
  function broadcast(msg: CoordMsg): void {
    try {
      channel.postMessage(msg)
    } catch {
      /* ignore */
    }
    if (ws && ws.readyState === WebSocket.OPEN) {
      try {
        ws.send(JSON.stringify(msg))
      } catch {
        /* ignore */
      }
    } else if (ws && ws.readyState === WebSocket.CONNECTING) {
      ws.addEventListener(
        'open',
        () => {
          try {
            ws?.send(JSON.stringify(msg))
          } catch {
            /* ignore */
          }
        },
        { once: true },
      )
    }
  }

  /* 等待对端让出的回调钩子（requestRelease 内赋值，两通道任一收到即触发） */
  let onAnyPortReleased: ((how: 'released' | 'busy') => void) | null = null

  /** 收到「对端要占用」：让出（由 handlers 关闭连接），回执已让出 */
  async function handleReleaseRequest(): Promise<void> {
    try {
      await handlers.onYieldRequested()
    } catch {
      /* 让出失败也回执，避免对端死等 */
    }
    await sleep(RELEASE_SETTLE_MS)
    broadcast({ type: 'port_released' })
  }

  function handleCoordMsg(msg: CoordMsg): void {
    if (!msg || !msg.type) return
    if (msg.type === 'release_port') {
      void handleReleaseRequest()
    } else if (msg.type === 'port_released') {
      onAnyPortReleased?.('released')
    } else if (msg.type === 'port_busy') {
      handlers.onBusy?.()
      onAnyPortReleased?.('busy')
    } else if (msg.type === 'peer_left') {
      /* App 离开中继：若本页面当前活跃，立即接管（不必等下次切焦点） */
      if (document.visibilityState === 'visible' && document.hasFocus()) {
        handlers.onBecameActive()
      }
    }
  }
  channel.onmessage = (e) => handleCoordMsg(e.data)

  function requestRelease(): Promise<ReleaseOutcome> {
    return new Promise((resolve) => {
      let replied = false
      const finish = (how: 'released' | 'busy') => {
        if (replied) return
        replied = true
        onAnyPortReleased = null
        if (how === 'busy') {
          resolve('busy')
          return
        }
        setTimeout(() => resolve('ok'), REPLY_SETTLE_MS)
      }
      onAnyPortReleased = finish
      connectBridge()
      broadcast({ type: 'release_port' })
      setTimeout(() => {
        if (!replied) {
          replied = true
          onAnyPortReleased = null
          resolve('ok')
        }
      }, RELEASE_TIMEOUT_MS)
    })
  }

  /* 焦点模型：window focus 覆盖「两个窗口并排都可见、只是切换焦点」的场景；
   * visibilitychange 覆盖「切标签页 / 最小化」的场景。 */
  const onFocus = () => handlers.onBecameActive()
  const onVisibility = () => {
    if (document.visibilityState === 'visible') handlers.onBecameActive()
  }
  window.addEventListener('focus', onFocus)
  document.addEventListener('visibilitychange', onVisibility)

  connectBridge()

  function dispose(): void {
    disposed = true
    window.removeEventListener('focus', onFocus)
    document.removeEventListener('visibilitychange', onVisibility)
    if (wsReconnectTimer) {
      clearTimeout(wsReconnectTimer)
      wsReconnectTimer = null
    }
    try {
      ws?.close()
    } catch {
      /* ignore */
    }
    ws = null
    channel.close()
  }

  return { requestRelease, dispose }
}
